import { falArrowDownWideShort } from 'shared/@generated/icons/falArrowDownWideShort';
import { falBell } from 'shared/@generated/icons/falBell';
import { falSlidersSimple } from 'shared/@generated/icons/falSlidersSimple';
import { useMemo, useRef, useState } from 'react';
import { ScrollView, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { Animal, Breed, BreedType, Gender, PetAge } from 'shared/@generated/models';
import markets from 'shared/@generated/markets.json';
import { BaseModalRef } from 'shared/components/base-modal';
import { Button } from 'shared/components/button';
import { BreedPickerPanel } from 'shared/components/breed-picker-panel';
import { DropdownSelect } from 'shared/components/form/dropdown-select';

import { LocationFilterPanel } from 'shared/components/location-filter-panel.web';
import { SearchModal } from 'shared/components/search-modal';
import { SvgImage } from 'shared/components/svg-image';
import { Text } from 'shared/components/text';
import {
  ActiveFilterItem,
  ActiveFilterList,
  FilterButton,
  FilterButtonRow,
  FilterModal,
  FilterModalFooter,
  FilterOption,
  FilterOverviewRow,
  SelectFilterPanel,
  SliderFilterPanel,
} from 'shared/components/web-filter-controls';
import { flags } from 'shared/library/i18n/flags';
import { LanguageCode } from 'shared/library/i18n/languages';
import { useFormatter } from 'shared/library/use-formatter';
import { useLocation } from 'shared/library/use-location';
import { useBreeds } from 'shared/state/breeds';
import { ListingsFilter, sortOptions, useListingsFilterStore } from 'shared/state/listings-filter';

type FilterPanel =
  | 'overview'
  | 'breeds'
  | 'country'
  | 'animal'
  | 'breedType'
  | 'seller'
  | 'gender'
  | 'petAge'
  | 'maxPrice'
  | 'distance';

type ListingsWebFilterControlsProps = {
  totalPets: number;
  boostedCount: number;
  initialBreeds?: Breed[];
  routeDefaults?: Partial<ListingsFilter>;
  onSelectBreed?: (breed: Breed, currentFilter?: ListingsFilter) => void;
  onSelectBreedQuery?: (value: string, currentFilter?: ListingsFilter) => void;
  onClearBreedSelection?: (currentFilter?: ListingsFilter) => void;
  onOpenChange?: (open: boolean) => void;
  showResultCount?: boolean;
};

type SelectOption<T> = {
  label: string;
  value: T | null;
};

const EMPTY_BREEDS: Breed[] = [];

export function ListingsWebFilterControls({
  totalPets,
  boostedCount,
  initialBreeds = EMPTY_BREEDS,
  routeDefaults,
  onSelectBreed,
  onSelectBreedQuery,
  onClearBreedSelection,
}: ListingsWebFilterControlsProps) {
  const { t } = useTranslation(['components', 'screens']);
  const { formatCurrency } = useFormatter();
  const currencySuffix = formatCurrency(0).replace(/[\d\s.,]/g, '');
  const priceMarks = [1000, 10000, 20000, 30000, 40000, 50000].map((value) => ({
    value,
    label: `${formatCurrency(value).replace(currencySuffix, '').trim()}${value === 50000 ? '+' : ''}`,
  }));
  const filter = useListingsFilterStore((state) => state.filter);
  const setFilter = useListingsFilterStore((state) => state.setFilter);
  const reset = useListingsFilterStore((state) => state.reset);
  const modalRef = useRef<BaseModalRef>(null);
  const [panel, setPanel] = useState<FilterPanel>('overview');
  const [breedQuery, setBreedQuery] = useState('');
  const { breeds: loadedBreeds, loading: breedsLoading } = useBreeds();
  const { location: selectedLocation, place: selectedPlace } = useLocation();
  const shouldNavigateOnBreedSelect = Boolean(onSelectBreed || onSelectBreedQuery);

  const allBreeds = useMemo(() => {
    const byId = new Map<number, Breed>();

    for (const breed of initialBreeds) byId.set(breed.id, breed);
    for (const breed of loadedBreeds) byId.set(breed.id, breed);

    return Array.from(byId.values());
  }, [initialBreeds, loadedBreeds]);

  const availableBreeds = useMemo(() => {
    const normalizedQuery = breedQuery.trim().toLocaleLowerCase();

    return allBreeds
      .filter((breed) => !filter.animal || breed.animal === filter.animal)
      .filter(
        (breed) => !normalizedQuery || breed.name.toLocaleLowerCase().includes(normalizedQuery),
      )
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [allBreeds, breedQuery, filter.animal]);

  const selectedBreeds = useMemo(
    () =>
      (filter.breeds ?? []).map((value) => ({
        value,
        label:
          typeof value === 'number'
            ? (allBreeds.find((breed) => breed.id === value)?.name ??
              t('components:listings_filter_form.selected_breed'))
            : value,
      })),
    [allBreeds, filter.breeds, t],
  );

  const countryOptions: FilterOption<string>[] = [
    { label: t('components:listings_filter_form.all_countries'), value: null },
    ...markets.map((market) => ({
      label: t(`common:enums.country_code.${market.country_code}`),
      value: market.country_code,
      icon: (
        <SvgImage
          src={flags[market.language_code as LanguageCode]}
          alt={''}
          containerClassName={'-mr-1.5 h-5 w-6'}
        />
      ),
    })),
  ];
  const animalOptions: SelectOption<Animal>[] = [
    { label: t('components:listings_filter_form.all_animals'), value: null },
    ...Object.values(Animal).map((value) => ({
      label: t(`common:enums.animal.${value}`),
      value,
    })),
  ];
  const breedTypeOptions: SelectOption<BreedType>[] = [
    { label: t('components:listings_filter_form.all_breed_types'), value: null },
    ...Object.values(BreedType)
      .filter((value) => value !== BreedType.Crossbreed)
      .map((value) => ({
        label: t(`common:enums.breed_type.${value}`),
        value,
      })),
  ];
  const genderOptions: SelectOption<Gender>[] = [
    { label: t('components:listings_filter_form.all_genders'), value: null },
    ...Object.values(Gender).map((value) => ({
      label: t(`common:enums.gender.${value}`),
      value,
    })),
  ];
  const petAgeOptions: SelectOption<PetAge>[] = [
    { label: t('components:listings_filter_form.all_ages'), value: null },
    ...Object.values(PetAge).map((value) => ({
      label: t(`common:enums.pet_age.${value}`),
      value,
    })),
  ];
  const sellerOptions: SelectOption<boolean>[] = [
    { label: t('components:listings_filter_form.all_sellers'), value: null },
    { label: t('components:listings_filter_form.only_breeders'), value: true },
  ];

  const hasLocationFilter =
    filter.latitude !== null && filter.longitude !== null && filter.maxDistanceInKm !== null;
  const hasDefaultLocation =
    routeDefaults?.latitude !== null &&
    routeDefaults?.latitude !== undefined &&
    routeDefaults.longitude !== null &&
    routeDefaults.longitude !== undefined &&
    routeDefaults.maxDistanceInKm !== null &&
    routeDefaults.maxDistanceInKm !== undefined;
  const matchesDefaultLocation =
    hasLocationFilter &&
    hasDefaultLocation &&
    filter.latitude === routeDefaults.latitude &&
    filter.longitude === routeDefaults.longitude &&
    filter.maxDistanceInKm === routeDefaults.maxDistanceInKm;
  const selectedPlaceMatchesFilter =
    selectedLocation?.latitude === filter.latitude &&
    selectedLocation?.longitude === filter.longitude;

  const selectedLabels = {
    breeds:
      selectedBreeds.length > 0
        ? selectedBreeds.map((breed) => breed.label).join(', ')
        : t('components:listings_filter_form.all_breeds'),
    country: selectedOptionLabel(countryOptions, filter.country),
    animal: selectedOptionLabel(animalOptions, filter.animal),
    breedType: selectedOptionLabel(breedTypeOptions, filter.breedType),
    seller: selectedOptionLabel(sellerOptions, filter.onlyBreeders),
    gender: selectedOptionLabel(genderOptions, filter.gender),
    petAge: selectedOptionLabel(petAgeOptions, filter.petAge),
    maxPrice:
      filter.maxPrice === null
        ? t('components:listings_filter_form.none')
        : formatCurrency(filter.maxPrice),
    distance: !hasLocationFilter
      ? t('components:listings_filter_form.none')
      : [selectedPlaceMatchesFilter ? selectedPlace : null, `${filter.maxDistanceInKm} km`]
          .filter(Boolean)
          .join(' · '),
  };

  const activeCategoryCount = [
    filter.breeds && filter.breeds.length > 0,
    filter.country,
    filter.animal,
    filter.breedType,
    filter.onlyBreeders,
    filter.gender,
    filter.petAge,
    filter.maxPrice,
    hasLocationFilter,
  ].filter(Boolean).length;

  function openPanel(nextPanel: FilterPanel) {
    setBreedQuery('');
    setPanel(nextPanel);
    modalRef.current?.open();
  }

  function clearAll() {
    if (shouldNavigateOnBreedSelect) {
      onClearBreedSelection?.();
      return;
    }

    reset();
  }

  function setBreeds(values: (string | number)[]) {
    setFilter({ breeds: values.length > 0 ? values : null });
  }

  function toggleBreed(breed: Breed) {
    if (shouldNavigateOnBreedSelect) {
      onSelectBreed?.(breed, filter);
      return;
    }

    const current = filter.breeds ?? [];
    setBreeds(
      current.includes(breed.id)
        ? current.filter((value) => value !== breed.id)
        : [...current, breed.id],
    );
  }

  function removeBreed(value: string | number) {
    if (shouldNavigateOnBreedSelect) {
      onClearBreedSelection?.({ ...filter, breeds: null, page: 1 });
      return;
    }

    setBreeds((filter.breeds ?? []).filter((breed) => breed !== value));
  }

  const modalTitle =
    panel === 'overview'
      ? t('components:listings_filter_form.filter_listings')
      : {
          breeds: t('components:listings_filter_form.breed'),
          country: t('components:listings_filter_form.country'),
          animal: t('components:listings_filter_form.animal'),
          breedType: t('components:listings_filter_form.breed_type'),
          seller: t('components:listings_filter_form.seller'),
          gender: t('components:listings_filter_form.gender'),
          petAge: t('components:listings_filter_form.age'),
          maxPrice: t('components:listings_filter_form.max_price'),
          distance: t('components:listings_filter_form.location'),
        }[panel];

  const activeFilters: ActiveFilterItem[] = [
    ...(filter.animal
      ? [
          {
            key: 'animal',
            label: selectedLabels.animal,
            onRemove: () => setFilter({ animal: null }),
          },
        ]
      : []),
    ...selectedBreeds.map((breed) => ({
      key: `breed-${String(breed.value)}`,
      label: typeof breed.value === 'string' ? `"${breed.label}"` : breed.label,
      onRemove: () => removeBreed(breed.value),
    })),
    ...(filter.country && filter.country !== routeDefaults?.country
      ? [
          {
            key: 'country',
            label: selectedLabels.country,
            onRemove: () => setFilter({ country: routeDefaults?.country ?? null }),
          },
        ]
      : []),
    ...(filter.breedType
      ? [
          {
            key: 'breedType',
            label: selectedLabels.breedType,
            onRemove: () => setFilter({ breedType: null }),
          },
        ]
      : []),
    ...(filter.onlyBreeders
      ? [
          {
            key: 'seller',
            label: selectedLabels.seller,
            onRemove: () => setFilter({ onlyBreeders: null }),
          },
        ]
      : []),
    ...(filter.gender
      ? [
          {
            key: 'gender',
            label: selectedLabels.gender,
            onRemove: () => setFilter({ gender: null }),
          },
        ]
      : []),
    ...(filter.petAge
      ? [
          {
            key: 'petAge',
            label: selectedLabels.petAge,
            onRemove: () => setFilter({ petAge: null }),
          },
        ]
      : []),
    ...(filter.maxPrice
      ? [
          {
            key: 'maxPrice',
            label: selectedLabels.maxPrice,
            onRemove: () => setFilter({ maxPrice: null }),
          },
        ]
      : []),
    ...(hasLocationFilter && !matchesDefaultLocation
      ? [
          {
            key: 'distance',
            label: selectedLabels.distance,
            onRemove: () =>
              setFilter({
                maxDistanceInKm: routeDefaults?.maxDistanceInKm ?? null,
                latitude: routeDefaults?.latitude ?? null,
                longitude: routeDefaults?.longitude ?? null,
              }),
          },
        ]
      : []),
  ];

  const activePanelFilter = activeFilters.find(({ key }) => key === panel);
  const clearDisabled =
    panel === 'overview'
      ? activeCategoryCount === 0
      : panel === 'breeds'
        ? selectedBreeds.length === 0
        : !activePanelFilter;

  function clearPanel() {
    if (panel === 'overview') {
      clearAll();
    } else if (panel === 'breeds') {
      if (shouldNavigateOnBreedSelect) {
        onClearBreedSelection?.({ ...filter, breeds: null, page: 1 });
      } else {
        setFilter({ breeds: null });
      }
    } else {
      activePanelFilter?.onRemove();
    }
  }

  return (
    <View className={'mt-3 md:mt-4'}>
      <FilterButtonRow>
        <FilterButton
          label={t('components:listings_filter_form.filter')}
          icon={falSlidersSimple}
          count={activeCategoryCount}
          active={activeCategoryCount > 0}
          testID={'listings-filter-trigger'}
          onPress={() => openPanel('overview')}
        />
        <FilterButton
          label={t('components:listings_filter_form.animal')}
          count={filter.animal ? 1 : 0}
          active={Boolean(filter.animal)}
          onPress={() => openPanel('animal')}
        />
        <FilterButton
          label={t('components:listings_filter_form.breed')}
          count={selectedBreeds.length}
          active={selectedBreeds.length > 0}
          onPress={() => openPanel('breeds')}
        />
        <FilterButton
          label={t('components:listings_filter_form.country')}
          count={filter.country ? 1 : 0}
          active={Boolean(filter.country)}
          testID={'listings-filter-country'}
          onPress={() => openPanel('country')}
        />
        <FilterButton
          label={t('components:listings_filter_form.location')}
          count={hasLocationFilter ? 1 : 0}
          active={hasLocationFilter}
          onPress={() => openPanel('distance')}
        />
        <FilterButton
          label={t('components:listings_filter_form.age')}
          count={filter.petAge ? 1 : 0}
          active={Boolean(filter.petAge)}
          onPress={() => openPanel('petAge')}
        />
        <FilterButton
          label={t('components:listings_filter_form.max_price')}
          count={filter.maxPrice ? 1 : 0}
          active={Boolean(filter.maxPrice)}
          onPress={() => openPanel('maxPrice')}
        />
        <FilterButton
          label={t('components:listings_filter_form.seller')}
          count={filter.onlyBreeders ? 1 : 0}
          active={Boolean(filter.onlyBreeders)}
          onPress={() => openPanel('seller')}
        />
      </FilterButtonRow>

      <ActiveFilterList items={activeFilters} clearLabel={t('common:clear')} onClear={clearAll} />

      <View
        className={
          'mt-3 min-w-0 flex-row flex-wrap items-center gap-2 pb-2 md:flex-nowrap md:gap-3 md:py-2'
        }
      >
        <Text
          className={'text-brand-brown-dark flex-none text-sm font-semibold md:text-base'}
          accessibilityLiveRegion={'polite'}
          numberOfLines={1}
        >
          {t('components:listings_filter_form.pets_found', { count: totalPets })}
        </Text>

        <SearchModal
          initialData={filter}
          source={'listings_results_toolbar'}
          resultsCount={totalPets}
          boostedCount={boostedCount}
          trigger={({ open }) => (
            <Button
              className={'flex-none'}
              icon={falBell}
              iconPosition={'left'}
              color={'white'}
              dense={true}
              center={true}
              accessibilityLabel={t('screens:listings.index_listings.save_search')}
              testID={'listings-save-search-toolbar'}
              onPress={open}
            >
              <Text className={'text-brand-brown-dark ml-1.5 text-sm font-semibold md:hidden'}>
                {t('screens:listings.index_listings.save_search_short')}
              </Text>
              <Text
                className={
                  'text-brand-brown-dark ml-1.5 hidden text-sm font-semibold md:flex md:text-base'
                }
              >
                {t('screens:listings.index_listings.save_search')}
              </Text>
            </Button>
          )}
        />

        <View className={'ml-auto flex-none'}>
          <DropdownSelect
            title={t('components:listings_filter_form.sort.recommended')}
            options={[
              {
                label: t('components:listings_filter_form.sort.recommended'),
                value: null,
              },
              ...sortOptions
                .filter((value) => value !== 'recommended')
                .map((value) => ({
                  label: t(`components:listings_filter_form.sort.${value}`),
                  value,
                })),
            ]}
            value={filter.sortBy}
            triggerIcon={falArrowDownWideShort}
            dense={true}
            bold={true}
            contentAlign={'end'}
            triggerClassName={'flex-none'}
            onChange={(sortBy) => setFilter({ sortBy })}
          />
        </View>
      </View>

      <FilterModal
        ref={modalRef}
        title={modalTitle}
        overview={panel === 'overview'}
        className={panel === 'breeds' ? 'md:h-[min(40rem,96dvh)]' : undefined}
        backLabel={t('components:listings_filter_form.all_filters')}
        onBack={() => setPanel('overview')}
        contentTestID={'listings-filter-modal'}
        footer={({ close }) => (
          <FilterModalFooter
            clearLabel={t('common:clear')}
            applyLabel={t('components:listings_filter_form.view_pets')}
            resultCount={totalPets}
            clearDisabled={clearDisabled}
            clearTestID={'listings-filter-clear'}
            applyTestID={'listings-filter-apply'}
            onClear={clearPanel}
            onApply={close}
          />
        )}
      >
        <View className={'min-h-0 flex-1'}>
          {panel !== 'overview' && (
            <Text className={'sr-only'}>
              {t('components:listings_filter_form.filter_listings')}
            </Text>
          )}
          {panel === 'overview' ? (
            <ScrollView contentContainerClassName={'p-3 md:px-5 md:py-4'}>
              <FilterOverviewRow
                label={t('components:listings_filter_form.breed')}
                value={selectedLabels.breeds}
                active={selectedBreeds.length > 0}
                onPress={() => setPanel('breeds')}
              />
              <FilterOverviewRow
                label={t('components:listings_filter_form.country')}
                value={selectedLabels.country}
                active={Boolean(filter.country)}
                onPress={() => setPanel('country')}
              />
              <FilterOverviewRow
                label={t('components:listings_filter_form.animal')}
                value={selectedLabels.animal}
                active={Boolean(filter.animal)}
                testID={'listings-filter-animal'}
                onPress={() => setPanel('animal')}
              />
              <FilterOverviewRow
                label={t('components:listings_filter_form.breed_type')}
                value={selectedLabels.breedType}
                active={Boolean(filter.breedType)}
                onPress={() => setPanel('breedType')}
              />
              <FilterOverviewRow
                label={t('components:listings_filter_form.seller')}
                value={selectedLabels.seller}
                active={Boolean(filter.onlyBreeders)}
                testID={'listings-filter-seller'}
                onPress={() => setPanel('seller')}
              />
              <FilterOverviewRow
                label={t('components:listings_filter_form.gender')}
                value={selectedLabels.gender}
                active={Boolean(filter.gender)}
                onPress={() => setPanel('gender')}
              />
              <FilterOverviewRow
                label={t('components:listings_filter_form.age')}
                value={selectedLabels.petAge}
                active={Boolean(filter.petAge)}
                onPress={() => setPanel('petAge')}
              />
              <FilterOverviewRow
                label={t('components:listings_filter_form.max_price')}
                value={selectedLabels.maxPrice}
                active={Boolean(filter.maxPrice)}
                onPress={() => setPanel('maxPrice')}
              />
              <FilterOverviewRow
                label={t('components:listings_filter_form.location')}
                value={selectedLabels.distance}
                active={hasLocationFilter}
                onPress={() => setPanel('distance')}
              />
            </ScrollView>
          ) : (
            <View className={'min-h-0 flex-1'}>
              {panel === 'breeds' && (
                <BreedPickerPanel
                  query={breedQuery}
                  setQuery={setBreedQuery}
                  breeds={availableBreeds}
                  allBreeds={allBreeds}
                  loading={breedsLoading}
                  selected={filter.breeds ?? []}
                  multiple={!shouldNavigateOnBreedSelect}
                  onToggle={toggleBreed}
                  onRemove={removeBreed}
                  onSelectQuery={(value) => {
                    if (shouldNavigateOnBreedSelect) {
                      onSelectBreedQuery?.(value, filter);
                    } else {
                      setBreeds([...(filter.breeds ?? []), value]);
                    }
                  }}
                />
              )}
              {panel === 'country' && (
                <SelectFilterPanel
                  options={countryOptions}
                  value={filter.country}
                  onChange={(country) =>
                    setFilter({ country: country ?? routeDefaults?.country ?? null })
                  }
                />
              )}
              {panel === 'animal' && (
                <SelectFilterPanel
                  options={animalOptions}
                  value={filter.animal}
                  onChange={(animal) => setFilter({ animal })}
                />
              )}
              {panel === 'breedType' && (
                <SelectFilterPanel
                  options={breedTypeOptions}
                  value={filter.breedType}
                  onChange={(breedType) => setFilter({ breedType })}
                />
              )}
              {panel === 'seller' && (
                <SelectFilterPanel
                  options={sellerOptions}
                  value={filter.onlyBreeders}
                  onChange={(onlyBreeders) => setFilter({ onlyBreeders })}
                />
              )}
              {panel === 'gender' && (
                <SelectFilterPanel
                  options={genderOptions}
                  value={filter.gender}
                  onChange={(gender) => setFilter({ gender })}
                />
              )}
              {panel === 'petAge' && (
                <SelectFilterPanel
                  options={petAgeOptions}
                  value={filter.petAge}
                  onChange={(petAge) => setFilter({ petAge })}
                />
              )}
              {panel === 'maxPrice' && (
                <View className={'px-5 py-8 md:px-8'}>
                  <SliderFilterPanel
                    value={filter.maxPrice}
                    valueLabel={
                      filter.maxPrice === null
                        ? t('components:listings_filter_form.no_max_price')
                        : formatCurrency(filter.maxPrice)
                    }
                    marks={priceMarks}
                    suffix={currencySuffix}
                    min={1000}
                    max={50000}
                    roundTo={1000}
                    accessibilityLabel={t('components:listings_filter_form.max_price')}
                    onChange={(maxPrice) =>
                      setFilter({ maxPrice: maxPrice === 50000 ? null : maxPrice })
                    }
                  />
                </View>
              )}
              {panel === 'distance' && (
                <LocationFilterPanel
                  coordinate={
                    filter.latitude !== null && filter.longitude !== null
                      ? { latitude: filter.latitude, longitude: filter.longitude }
                      : null
                  }
                  maxDistanceInKm={filter.maxDistanceInKm}
                  country={filter.country}
                  onChange={setFilter}
                />
              )}
            </View>
          )}
        </View>
      </FilterModal>
    </View>
  );
}

function selectedOptionLabel<T>(options: SelectOption<T>[], value: T | null) {
  return options.find((option) => option.value === value)?.label ?? options[0]!.label;
}
